import { useState, useEffect } from "react";
import { ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const defaultSlides = [
  {
    id: "about",
    image: "/images/hero-about.jpg",
    label: "Discover MOVI",
    title: "MOVI",
    subtitle: "An international and interdisciplinary initiative to bring attention to and promote research about sex differences and human health",
    href: "#about",
  },
  {
    id: "news",
    image: "/images/hero-news-movi.jpg",
    label: "Latest Updates",
    title: "NEWS",
    subtitle: "Stay informed about the latest research developments, events, and collaborations in our network",
    href: "#news",
  },
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState(defaultSlides);

  useEffect(() => {
    const fetchMeetingImage = async () => {
      const { data } = await supabase
        .from("news_items")
        .select("highlight_image_url")
        .eq("category", "Meeting")
        .order("display_order", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data?.highlight_image_url) {
        setSlides(prev => prev.map(slide => 
          slide.id === "news" 
            ? { ...slide, image: data.highlight_image_url! }
            : slide
        ));
      }
    };
    fetchMeetingImage();
  }, []);

  const scrollToSection = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[85vh] overflow-hidden rounded-b-[60px]">
      {/* Background Images with Cross-fade */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            width={1812}
            height={1080}
            className="w-full h-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-primary/60" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 min-h-[85vh] flex items-center">
        <div className="w-[70%] mx-auto">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute transition-all duration-700 ease-out ${
                index === currentSlide
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-16 pointer-events-none"
              }`}
            >
              <button
                onClick={() => scrollToSection(slide.href)}
                className={`mb-4 px-3 py-1.5 text-xs font-semibold tracking-widest text-primary-foreground bg-transparent border-2 border-primary-foreground rounded-sm hover:bg-primary-foreground/10 transition-all duration-300 ${
                  index === currentSlide ? "animate-fade-in" : ""
                }`}
                style={{ animationDelay: "0.2s", animationFillMode: "both" }}
              >
                {slide.label}
              </button>
              <h1 
                className={`text-4xl md:text-6xl lg:text-7xl font-medium text-primary-foreground mb-4 ${
                  index === currentSlide ? "animate-fade-in" : ""
                }`}
                style={{ animationDelay: "0.4s", animationFillMode: "both" }}
              >
                {slide.title}
              </h1>
              <p 
                className={`text-lg md:text-xl text-primary-foreground/80 mb-6 max-w-2xl ${
                  index === currentSlide ? "animate-fade-in" : ""
                }`}
                style={{ animationDelay: "0.6s", animationFillMode: "both" }}
              >
                {slide.subtitle}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-20 left-0 right-0 z-20">
        <div className="w-[70%] mx-auto px-6 flex justify-end gap-1.5">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(index)}
              className="p-1 transition-all duration-300"
              aria-label={`Go to ${slide.title} slide`}
            >
              <span
              className={`block rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "w-3.5 h-3.5 bg-primary-foreground"
                  : "w-2.5 h-2.5 bg-primary-foreground/40 hover:bg-primary-foreground/60"
              }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => scrollToSection("#about")}
        aria-label="Scroll to About section"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-primary-foreground/60 hover:text-primary-foreground transition-colors animate-bounce"
      >
        <ArrowDown size={28} />
      </button>

    </section>
  );
};
